"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { href: '/homepage', label: '首頁', labelEn: 'Home' },
  { href: '/about', label: '關於我們', labelEn: 'About' },
  { href: '/business-scope', label: '業務範圍', labelEn: 'Services' },
  { href: '/registration-center', label: '報名中心', labelEn: 'Register' },
  { href: '/contact', label: '聯繫我們', labelEn: 'Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-container mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2.5 flex-shrink-0">
            <AppLogo
              src="/assets/images/beb20884-3324-4758-9eef-42340ec5b17d-1773145188404.jpg"
              size={36}
            />
            <div className="flex flex-col leading-none">
              <span className="font-heading text-sm font-700 text-primary tracking-tight hidden sm:block">
                Anri Design
              </span>
              <span className="font-mono text-[9px] text-muted tracking-widest uppercase hidden sm:block">
                Interior Studio
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`relative px-4 py-2 font-body text-sm font-medium transition-colors duration-200 group ${
                    isActive ? 'text-accent' : 'text-secondary hover:text-foreground'
                  }`}
                >
                  <span>{link?.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-4 right-4 h-px bg-accent" />
                  )}
                  <span className="absolute bottom-0 left-4 right-4 h-px bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </Link>
              );
            })}
          </nav>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-3">
            <Link
              href="/registration-center"
              className="hidden lg:inline-flex items-center gap-2 bg-primary text-white text-xs font-heading font-600 tracking-widest uppercase px-5 py-2.5 hover:bg-accent transition-colors duration-200"
              style={{ borderRadius: '2px' }}
            >
              免費諮詢
            </Link>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden flex flex-col gap-1.5 p-2 group"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-px bg-primary transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
              <span className={`block h-px bg-primary transition-all duration-300 ${menuOpen ? 'opacity-0 w-0' : 'w-4'}`} />
              <span className={`block w-6 h-px bg-primary transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-primary/90 backdrop-blur-lg"
          onClick={() => setMenuOpen(false)}
        />
        <nav className="absolute top-16 left-0 right-0 bg-background border-b border-border">
          {navLinks?.map((link, i) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={link?.href}
                href={link?.href}
                className={`flex items-center justify-between px-6 py-4 border-b border-border font-body text-sm font-medium transition-colors ${
                  isActive ? 'text-accent bg-surface' : 'text-foreground hover:bg-surface'
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <span>{link?.label}</span>
                <span className="font-mono text-xs text-muted">{link?.labelEn}</span>
              </Link>
            );
          })}
          <div className="p-4">
            <Link
              href="/registration-center"
              className="btn-primary w-full justify-center"
            >
              免費諮詢
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
}